import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import RequirePopup from './require-popup'
import { requireDataset } from '../../actions/user-action'

const filters = ['All', 'Recent', 'Conference', 'Dissertation', 'Award']

function getYear(publication) {
  const source = `${publication?.conference?.title || ''} ${publication?.description || ''}`
  const match = source.match(/(?:19|20)\d{2}/)
  return match ? Number(match[0]) : 0
}

function getType(publication) {
  if (/dissertation/i.test(publication.description || '')) return 'Dissertation'
  if (publication.remark) return 'Award'
  if (publication.conference) return 'Conference'
  return 'Other'
}

function venueLine(publication) {
  if (publication.conference) {
    const parts = [publication.conference.title, publication.conference.location].filter(Boolean)
    return parts.join(' · ')
  }
  return publication.description || ''
}

function PublicationEntry({ publication, onRequire }) {
  const { name, author, description, conference, remark, image, links, projectURL } = publication
  const meta = venueLine(publication)
  const showDescription = description && !/dissertation/i.test(description)

  return (
    <PublicationItem>
      {image && <PublicationImage src={image} alt="" loading="lazy" aria-hidden="true" />}
      <PublicationContent>
        <PublicationHeader>
          <PublicationTitle
            target="_blank"
            rel="noopener noreferrer"
            href={projectURL}
          >
            {name}
          </PublicationTitle>
          {remark && <Remark>{remark}</Remark>}
        </PublicationHeader>

        <PublicationAuthor>{author}</PublicationAuthor>
        {meta && <PublicationMeta>{meta}</PublicationMeta>}
        {conference?.name && <VenueName>{conference.name}</VenueName>}
        {showDescription && <PublicationDescription>{description}</PublicationDescription>}

        {Object.keys(links || {}).length > 0 && (
          <PublicationActions>
            {Object.entries(links || {}).map(([label, link]) => {
              if (typeof link === 'string') {
                return (
                  <a key={label} target="_blank" rel="noopener noreferrer" className="button" href={link}>
                    {label}
                  </a>
                )
              }
              if (link?.require) {
                return (
                  <button type="button" key={label} onClick={() => onRequire(link)}>
                    {label}
                  </button>
                )
              }
              return null
            })}
          </PublicationActions>
        )}
      </PublicationContent>
    </PublicationItem>
  )
}

export default function Publication(){
  const [publications, setPublications] = useState([])
  const [popup, setPopup] = useState(false)
  const [activeFilter, setActiveFilter] = useState('All')

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/data/publications.json`)
      .then((response) => response.text())
      .then((text) => JSON.parse(text.replace(/%PUBLIC_URL%/g, process.env.PUBLIC_URL)))
      .then((items) => setPublications(items.sort((a, b) => getYear(b) - getYear(a))))
      .catch((error) => console.error(error))
  }, [])

  const onPopupClose = (accept) => {
    const { require } = popup
    if (accept && accept[require]) requireDataset(accept)
    setPopup(false)
  }

  const latestYear = useMemo(() => Math.max(0, ...publications.map(getYear)), [publications])
  // Keep highlights data-driven: the list always uses the newest records after sorting.
  // CSS keeps the section to one row by showing 4 / 2 / 1 items by screen width.
  const highlights = useMemo(() => publications.slice(0, 4), [publications])

  const filtered = useMemo(() => {
    return publications.filter((publication) => {
      const year = getYear(publication)
      const type = getType(publication)
      return (
        activeFilter === 'All' ||
        (activeFilter === 'Recent' && year >= latestYear - 3) ||
        activeFilter === type
      )
    })
  }, [activeFilter, latestYear, publications])

  const grouped = useMemo(() => {
    return filtered.reduce((groups, publication) => {
      const year = getYear(publication) || 'Earlier'
      if (!groups[year]) groups[year] = []
      groups[year].push(publication)
      return groups
    }, {})
  }, [filtered])

  return (
    <Container id="publications" className="section section-publication">
      {popup && (
        <RequirePopup
          message={popup.message}
          require={popup.require}
          perpose={popup.perpose}
          onClose={onPopupClose}
        />
      )}

      <div className="container">
        <SectionIntro>
          <Title>Publications</Title>
          <Count>{publications.length} works</Count>
        </SectionIntro>

        {highlights.length > 0 && (
          <Highlights aria-labelledby="recent-heading">
            <Subheading id="recent-heading">Recent highlights</Subheading>
            <HighlightGrid>
              {highlights.map((publication) => (
                <HighlightCard key={publication.name} href={publication.projectURL} target="_blank" rel="noopener noreferrer">
                  <HighlightYear>{getYear(publication)}</HighlightYear>
                  <HighlightTitle>{publication.name}</HighlightTitle>
                  <HighlightVenue>{publication.conference?.title || publication.description}</HighlightVenue>
                </HighlightCard>
              ))}
            </HighlightGrid>
          </Highlights>
        )}

        <ArchiveHeader>
          <Subheading>Publication archive</Subheading>
        </ArchiveHeader>

        <FilterBar aria-label="Filter publications">
          {filters.map((filter) => (
            <FilterButton
              type="button"
              key={filter}
              $active={activeFilter === filter}
              aria-pressed={activeFilter === filter}
              onClick={() => setActiveFilter(filter)}
            >
              {filter}
            </FilterButton>
          ))}
        </FilterBar>

        {Object.keys(grouped).length ? (
          <YearGroups>
            {Object.entries(grouped)
              .sort(([a], [b]) => Number(b) - Number(a))
              .map(([year, items]) => (
                <YearGroup key={year}>
                  <YearHeading>
                    <span>{year}</span>
                    <small>{items.length}</small>
                  </YearHeading>
                  <PublicationList>
                    {items.map((publication) => (
                      <PublicationEntry
                        key={`${year}-${publication.name}`}
                        publication={publication}
                        onRequire={(link) => setPopup(link)}
                      />
                    ))}
                  </PublicationList>
                </YearGroup>
              ))}
          </YearGroups>
        ) : (
          <EmptyState>No publications match this filter.</EmptyState>
        )}
      </div>
    </Container>
  )
}

const Container = styled.section`
  scroll-margin-top: 72px;
  padding: clamp(40px, 6vw, 72px) 0;
  background-color: rgba(var(--background), 0.82);
`
const SectionIntro = styled.div`
  display: flex;
  gap: 24px;
  align-items: end;
  justify-content: space-between;
`
const Title = styled.h2`
  margin: 0;
  font-family: 'Concert One';
  font-size: clamp(2rem, 5vw, 3.2rem);
`
const Count = styled.span`
  color: var(--primary-color);
  font-weight: 700;
`
const Highlights = styled.div`
  margin-top: 28px;
`
const Subheading = styled.h3`
  margin: 0;
  color: var(--text-color);
  font-family: 'Concert One';
  font-size: clamp(1.35rem, 3vw, 1.75rem);
`
const HighlightGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;

  @media (max-width: 991px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    & > :nth-child(n + 3) { display: none; }
  }

  @media (max-width: 575px) {
    grid-template-columns: 1fr;
    & > :nth-child(n + 2) { display: none; }
  }
`
const HighlightCard = styled.a`
  min-width: 0;
  padding: 16px;
  background: linear-gradient(145deg, rgba(255,255,255,.96), rgba(255,255,255,.72));
  border: 1px solid rgba(85,91,110,.1);
  border-radius: 14px;
  color: var(--text-color);
  text-decoration: none;
  transition: transform 160ms ease, border-color 160ms ease;
  &:hover, &:focus-visible { color: var(--text-color); transform: translateY(-2px); border-color: rgba(var(--assent-color-rgb), .35); }
`
const HighlightYear = styled.span`
  color: var(--assent-color);
  font-size: 13px;
  font-weight: 700;
`
const HighlightTitle = styled.strong`
  display: -webkit-box;
  margin-top: 5px;
  overflow: hidden;
  font-size: 16px;
  line-height: 1.3;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
`
const HighlightVenue = styled.span`
  display: block;
  margin-top: 8px;
  color: var(--primary-color);
  font-size: 13px;
`
const ArchiveHeader = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  margin-top: 42px;
  @media (max-width: 767px) { align-items: stretch; flex-direction: column; }
`
const FilterBar = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
  margin-top: 14px;

  @media (max-width: 767px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`
const FilterButton = styled.button`
  width: 100%;
  min-width: 0;
  min-height: 42px;
  padding: 7px 14px;
  color: ${({ $active }) => ($active ? '#fff' : 'var(--text-color)')};
  background: ${({ $active }) => ($active ? 'var(--assent-color)' : 'rgba(255,255,255,.84)')};
  border: 1px solid ${({ $active }) => ($active ? 'var(--assent-color)' : 'rgba(85,91,110,.14)')};
  border-radius: 999px;
  font-weight: 600;
  white-space: nowrap;

  @media (max-width: 480px) {
    padding-inline: 10px;
    font-size: 13px;
  }
`
const YearGroups = styled.div`
  margin-top: 26px;
`
const YearGroup = styled.section`
  display: grid;
  grid-template-columns: 90px minmax(0, 1fr);
  gap: 24px;
  padding: 22px 0;
  border-top: 1px solid rgba(85,91,110,.13);
  @media (max-width: 767px) { grid-template-columns: 1fr; gap: 5px; padding: 18px 0; }
`
const YearHeading = styled.h4`
  position: sticky;
  top: 84px;
  align-self: start;
  display: flex;
  gap: 7px;
  align-items: baseline;
  margin: 0;
  color: var(--assent-color);
  font-family: 'Concert One';
  font-size: 1.7rem;
  small { color: var(--primary-color); font-family: inherit; font-size: .75rem; }
  @media (max-width: 767px) { position: static; }
`
const PublicationList = styled.div`
  min-width: 0;
`
const PublicationItem = styled.article`
  display: grid;
  grid-template-columns: 82px minmax(0, 1fr);
  gap: 15px;
  padding: 14px 0;
  border-bottom: 1px solid rgba(85,91,110,.1);
  &:first-child { padding-top: 0; }
  &:last-child { border-bottom: 0; }
  @media (max-width: 767px) { display: block; }
`
const PublicationImage = styled.img`
  width: 82px;
  height: 82px;
  object-fit: contain;
  background: transparent;
  border-radius: 9px;
  @media (max-width: 767px) { display: none; }
`
const PublicationContent = styled.div`min-width: 0;`
const PublicationHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px 10px;
  align-items: flex-start;
`
const PublicationTitle = styled.a`
  flex: 1 1 420px;
  color: var(--text-color);
  font-family: 'Concert One';
  font-size: clamp(17px, 2vw, 20px);
  line-height: 1.25;
  text-decoration: none;
  overflow-wrap: anywhere;
  &:hover, &:focus-visible { color: var(--assent-color); }
`
const Remark = styled.span`
  padding: 3px 8px;
  color: #fff;
  background: var(--assent-color);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
`
const PublicationAuthor = styled.p`
  margin: 4px 0 0;
  color: var(--primary-color);
  font-size: 14px;
  line-height: 1.35;
`
const PublicationMeta = styled.p`
  margin: 4px 0 0;
  color: var(--text-color);
  font-size: 14px;
  font-weight: 700;
`
const VenueName = styled.p`
  margin: 1px 0 0;
  color: var(--text-color);
  font-size: 13px;
  line-height: 1.35;
`
const PublicationDescription = styled.p`
  margin: 5px 0 0;
  color: var(--text-color);
  font-size: 14px;
  line-height: 1.4;
`
const PublicationActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  & .button, & button {
    min-height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 5px 11px;
    color: var(--assent-color);
    background: transparent;
    border: 1px solid rgba(var(--assent-color-rgb), .5);
    border-radius: 999px;
    font: inherit;
    font-size: 12px;
    font-weight: 700;
    text-decoration: none;
    cursor: pointer;
  }
  & .button:hover, & .button:focus-visible, & button:hover, & button:focus-visible { color: #fff; background: var(--assent-color); }
  @media (max-width: 575px) { & .button, & button { min-height: 40px; padding-inline: 13px; } }
`
const EmptyState = styled.p`
  margin-top: 24px;
  padding: 28px;
  text-align: center;
  background: rgba(255,255,255,.72);
  border-radius: 14px;
`
